import { createElement } from 'react';
import type {
  InventoryVehicle,
  InventoryFilters,
  InventoryTableRow,
} from '../../api/services/lupp/inventory/dto.inventory';
import type { ParsedFilterOptions } from '../../api/services/lupp/inventory/transformers.inventory';
import {
  formatPrice,
  Typography,
  type FilterState,
  type TableColumn,
  type ImageProps,
} from '@lucid/lego';
import {
  AVAILABILITY_OPTIONS,
  AVAILABILITY_LABEL_KEYS,
  AIR_MODEL_YEAR_OPTIONS,
  GRAVITY_MODEL_YEAR_OPTIONS,
  getTrimOptionsForModel,
  buildPackageFilterGroups,
} from '../../api/services/lupp/inventory/filterOptions.inventory';
import { getCurrencyCodeForLocale } from '../../utils/tools';
import { isMy27Enabled } from '../../config/featureFlags';
import { PACKAGE_FILTER_FIELD_MAPPING } from './constants';

// Re-export InventoryTableRow for consumers that import from here
export type { InventoryTableRow };

/**
 * Creates an empty InventoryTableRow with all fields defaulted.
 * Use with partial overrides for placeholder rows (e.g., URL deep-link, shortcode checkout).
 * @param overrides - Fields to override on the empty row
 * @returns A complete InventoryTableRow
 */
export const createEmptyVehicleRow = (
  overrides: Partial<InventoryTableRow> = {}
): InventoryTableRow => ({
  vin: '',
  webLink: '',
  batchNumber: '',
  availability: '',
  locationName: '',
  city: '',
  country: '',
  model: '',
  trim: '',
  drivetrain: '',
  exteriorColor: '',
  exteriorColorCode: '',
  interiorColor: '',
  interiorColorCode: '',
  appearance: '',
  appearanceCode: '',
  roof: '',
  wheels: '',
  wheelsCode: '',
  year: '',
  adas: '',
  adasCode: '',
  soundSystem: '',
  soundSystemCode: '',
  frontSeats: '',
  frontSeatsCode: '',
  rearSeats: '',
  comfortPackage: '',
  dynamicHandlingPackage: '',
  technologyPackage: '',
  towingPackage: '',
  power: '',
  range: '',
  zeroToHundredKmh: '',
  transportationConfirmed: '',
  inTransitToLocation: '',
  activeFactoryCampaigns: '',
  uuid: '',
  shortcode: '',
  condition: 'new',
  ...overrides,
});

const getAvailabilityRank = (availability: string): number | null => {
  const normalizedAvailability = availability
    .trim()
    .replace(/^["'“”]+|["'“”]+$/g, '')
    .trim()
    .toUpperCase();

  if (normalizedAvailability === 'AVAILABLE NOW') return 0;

  const weekMatch = normalizedAvailability.match(/^AVAILABLE IN\s+(\d+)\s+WEEKS?$/);
  return weekMatch ? Number(weekMatch[1]) : null;
};

/** Sorts availability by soonest/latest delivery while keeping unknown values last. */
export const sortInventoryRowsByAvailability = (
  rows: InventoryTableRow[],
  direction: 'asc' | 'desc'
): InventoryTableRow[] =>
  rows
    .map((row, index) => ({ row, index, rank: getAvailabilityRank(row.availability) }))
    .sort((left, right) => {
      if (left.rank === null && right.rank === null) return left.index - right.index;
      if (left.rank === null) return 1;
      if (right.rank === null) return -1;

      const rankDifference = left.rank - right.rank;
      return rankDifference === 0
        ? left.index - right.index
        : rankDifference * (direction === 'asc' ? 1 : -1);
    })
    .map(({ row }) => row);

/**
 * Convert a simple image URL to ImageProps format for swatch filters
 */
const urlToImageProps = (url: string, alt: string): ImageProps => ({
  uri_desktop: { '1x': url, '2x': url },
  uri_mobile: { '1x': url, '2x': url },
  alt,
});

/**
 * Renders a plain string cell value, falling back to a dash when empty/null/undefined.
 * Wrapped in Typography (matching the Table's default cell rendering) so columns using a
 * custom `render` keep the same font size/style as columns without one.
 */
const renderTextOrDash = (value: unknown) =>
  createElement(
    Typography,
    { variant: 'body', size: 'small', color: 'black' },
    typeof value === 'string' && value ? value : '-'
  );

/**
 * Filter options based on selected trims availability
 * Returns only options that are available for at least one of the selected trims
 */
const filterOptionsByTrims = <T extends { availableTrims?: string[] }>(
  options: T[],
  selectedTrims: string[] | undefined,
  isGravity = false
): T[] => {
  // If no trims selected, show all options
  if (!selectedTrims || selectedTrims.length === 0) return options;

  const availabilityTrims = new Set(selectedTrims);
  if (isGravity) {
    if (availabilityTrims.has('TP')) availabilityTrims.add('AT');
    if (availabilityTrims.has('TU')) availabilityTrims.add('GT');
  }

  return options.filter((opt) => {
    // If option has no trim restrictions, it's available for all trims
    if (!opt.availableTrims || opt.availableTrims.length === 0) return true;
    // Check if option is available for any of the selected trims
    return opt.availableTrims.some((trim) => availabilityTrims.has(trim));
  });
};

/**
 * Labels type for i18n support
 */
export type InventoryLabels = Record<string, string> | undefined;

/**
 * Get model year filter options based on selected model
 * @param isGravity - Whether the Gravity model is selected
 * @returns Year filter options for the selected model
 */
const getModelYearOptions = (
  isGravity: boolean,
  isImporter?: boolean
): { value: string; label: string }[] => {
  let options = isGravity ? GRAVITY_MODEL_YEAR_OPTIONS : AIR_MODEL_YEAR_OPTIONS;
  if (isImporter && !isGravity) {
    options = [
      { label: '2027', value: '2027' },
      { label: '2026', value: '2026' },
    ];
  }
  // MY27 (2027) model year is hidden until the MY27 feature flag is enabled.
  if (!isMy27Enabled()) {
    options = options.filter((opt) => opt.value !== '2027');
  }
  return options.map((opt) => ({ value: opt.value, label: opt.label }));
};

const getPackageFilterColumns = (
  prCodes: ParsedFilterOptions | undefined,
  isGravity: boolean,
  labels: InventoryLabels
): TableColumn<InventoryTableRow>[] => {
  const groupLabels = {
    comfort: labels?.['filter.packages.comfort'] ?? 'Comfort & Convenience Package',
    handling: labels?.['filter.packages.handling'] ?? 'Dynamic Handling Package',
    technology: labels?.['filter.packages.technology'] ?? 'Technology Package',
    towing: labels?.['filter.packages.towing'] ?? 'Towing Package',
  };
  const groups = buildPackageFilterGroups(
    prCodes,
    isGravity,
    labels?.['filter.packages.withoutComfortPackage'],
    groupLabels
  );
  const groupsByKey = new Map(groups.map((group) => [group.key, group]));

  return [
    { key: 'packageComfort', title: groupLabels.comfort },
    { key: 'packageHandling', title: groupLabels.handling },
    { key: 'packageTechnology', title: groupLabels.technology },
    { key: 'packageTowing', title: groupLabels.towing },
  ].map(({ key, title }) => {
    const group = groupsByKey.get(key);
    return {
      key,
      title,
      visible: false,
      hideable: false,
      ...(group && {
        filter: {
          type: 'checkbox' as const,
          options: group.options.map(({ value, label }) => ({ value, label })),
        },
      }),
    };
  });
};

/**
 * Column definitions for the inventory table (base columns without dynamic filters)
 */
export const getInventoryColumns = (
  prCodes: ParsedFilterOptions | undefined,
  isGravity: boolean,
  selectedTrims?: string[],
  labels?: InventoryLabels,
  locale?: string,
  isImporter?: boolean,
  selectedModelYears?: string[]
): TableColumn<InventoryTableRow>[] =>
  [
    {
      key: 'vin',
      title: labels?.['configurator.table.vin'] ?? 'VIN',
      width: '180px',
      sortable: true,
    },
    !isImporter && {
      key: 'webLink',
      title: labels?.['configurator.table.webLink'] ?? 'Web Link',
      width: '100px',
      sortable: false,
    },
    {
      key: 'batchNumber',
      title: labels?.['configurator.table.batchNumber'] ?? 'Batch #',
      width: '120px',
      sortable: true,
    },
    // Filters ordered based on design: Model, Condition, Trim, Availability, Exterior, Interior, Wheels, Appearance, Driver Assistance, Sound System, Seating
    {
      key: 'model',
      title: labels?.['configurator.column.model'] ?? 'Model',
      width: '100px',
      sortable: true,
      filter: {
        type: 'radio',
        // Model options are always Air/Gravity - not from prCodes which includes bundle types
        options: [
          { value: 'AIR', label: labels?.['util.air'] ?? 'Air' },
          { value: 'GRAVITY', label: labels?.['util.gravity'] ?? 'Gravity' },
        ],
      },
    },
    {
      key: 'condition',
      title: labels?.['configurator.column.condition'] ?? 'Condition',
      width: '120px',
      sortable: true,
      render: (value: unknown) =>
        typeof value === 'string' && value
          ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
          : '-',
    },
    {
      key: 'year',
      title: labels?.['configurator.column.modelYear'] ?? 'Model Year',
      width: '120px',
      sortable: true,
      filter: {
        type: 'checkbox',
        options: getModelYearOptions(isGravity, isImporter),
      },
    },
    {
      key: 'trim',
      title: labels?.['configurator.column.trim'] ?? 'Trim',
      width: '120px',
      sortable: true,
      filter: {
        type: 'checkbox',
        options: getTrimOptionsForModel(isGravity, prCodes, selectedModelYears).map((opt) => ({
          value: opt.value,
          label: opt.label,
        })),
      },
    },
    {
      key: 'price',
      title: labels?.['configurator.column.price'] ?? 'Price',
      width: '140px',
      sortable: true,
      render: (value: unknown) =>
        value == null
          ? '-'
          : formatPrice(value as number, {
              locale: locale ?? 'de-de',
              currency: getCurrencyCodeForLocale(locale ?? 'de-de'),
            }),
    },
    {
      key: 'availability',
      title: labels?.['configurator.column.availability'] ?? 'Availability',
      width: '140px',
      sortable: true,
      filter: {
        type: 'checkbox',
        options: AVAILABILITY_OPTIONS.map((opt) => ({
          value: opt.value,
          label: labels?.[AVAILABILITY_LABEL_KEYS[opt.value]] ?? opt.label,
        })),
      },
    },
    {
      key: 'locationName',
      title: labels?.['configurator.column.locationName'] ?? 'Location Name',
      width: '160px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'city',
      title: labels?.['configurator.column.city'] ?? 'Location City',
      width: '120px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'country',
      title: labels?.['configurator.column.country'] ?? 'Location Country',
      width: '120px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'drivetrain',
      title: labels?.['configurator.column.drivetrain'] ?? 'Drivetrain',
      width: '120px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'exteriorColor',
      title: labels?.['configurator.column.exteriorColor'] ?? 'Exterior Color',
      width: '160px',
      sortable: true,
      filter: {
        type: 'swatch',
        swatchShape: 'circle',
        options: filterOptionsByTrims(prCodes?.exteriorColor || [], selectedTrims, isGravity).map(
          (opt) => ({
            value: opt.value,
            label: opt.label,
            image: opt.image ? urlToImageProps(opt.image, opt.label) : undefined,
          })
        ),
      },
    },
    {
      key: 'interiorColor',
      title: labels?.['configurator.column.interiorTheme'] ?? 'Interior Theme',
      width: '200px',
      sortable: true,
      filter: {
        type: 'swatch',
        swatchShape: 'rectangle',
        options: filterOptionsByTrims(prCodes?.interiorTheme || [], selectedTrims, isGravity).map(
          (opt) => ({
            value: opt.value,
            label: opt.label,
            image: opt.image ? urlToImageProps(opt.image, opt.label) : undefined,
          })
        ),
      },
    },
    {
      key: 'wheels',
      title: labels?.['configurator.column.wheels'] ?? 'Wheels',
      width: '180px',
      sortable: true,
      filter: {
        type: 'swatch',
        swatchShape: 'circle',
        options: filterOptionsByTrims(prCodes?.wheels || [], selectedTrims, isGravity).map(
          (opt) => ({
            value: opt.value,
            label: opt.label,
            image: opt.image ? urlToImageProps(opt.image, opt.label) : undefined,
          })
        ),
      },
    },
    {
      key: 'appearance',
      title: labels?.['configurator.column.appearance'] ?? 'Appearance',
      width: '180px',
      sortable: true,
      filter: {
        type: 'swatch',
        swatchShape: 'rectangle',
        options: filterOptionsByTrims(prCodes?.appearance || [], selectedTrims, isGravity).map(
          (opt) => ({
            value: opt.value,
            label: opt.label,
            image: opt.image ? urlToImageProps(opt.image, opt.label) : undefined,
          })
        ),
      },
    },
    {
      key: 'roof',
      title: labels?.['configurator.column.roof'] ?? 'Roof',
      width: '120px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'adas',
      title: labels?.['configurator.column.driverAssistance'] ?? 'Driver Assistance',
      width: '160px',
      sortable: true,
      render: renderTextOrDash,
      filter: {
        type: 'checkbox',
        options: (prCodes?.driverAssistance || []).map((opt) => ({
          value: opt.value,
          label: opt.label,
        })),
      },
    },
    {
      key: 'soundSystem',
      title: labels?.['configurator.column.soundSystem'] ?? 'Sound System',
      width: '140px',
      sortable: true,
      render: renderTextOrDash,
      filter: {
        type: 'checkbox',
        options: (prCodes?.soundSystem || []).map((opt) => ({
          value: opt.value,
          label: opt.label,
        })),
      },
    },
    {
      key: 'frontSeats',
      title: labels?.['configurator.column.seating'] ?? 'Seating',
      width: '280px',
      sortable: true,
      render: renderTextOrDash,
      filter: {
        type: 'checkbox',
        options: (prCodes?.seating || []).map((opt) => ({ value: opt.value, label: opt.label })),
      },
    },
    isGravity && {
      key: 'rearSeats',
      title: labels?.['configurator.column.rearSeats'] ?? 'Rear Seats',
      width: '280px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'comfortPackage',
      title: labels?.['configurator.column.comfortPackage'] ?? 'Comfort & Convenience Package',
      width: '260px',
      sortable: true,
      render: renderTextOrDash,
    },
    ...getPackageFilterColumns(prCodes, isGravity, labels),
    {
      key: 'dynamicHandlingPackage',
      title: labels?.['configurator.column.dynamicHandlingPackage'] ?? 'Dynamic Handling Package',
      width: '240px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'technologyPackage',
      title: labels?.['configurator.column.technologyPackage'] ?? 'Technology Package',
      width: '220px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'towingPackage',
      title: labels?.['configurator.column.towingPackage'] ?? 'Towing Package',
      width: '180px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'range',
      title: labels?.['configurator.column.range'] ?? 'Range',
      width: '120px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'power',
      title: labels?.['configurator.column.power'] ?? 'Power',
      width: '120px',
      sortable: true,
      render: renderTextOrDash,
    },
    {
      key: 'zeroToHundredKmh',
      title: labels?.['configurator.column.zeroToHundredKmh'] ?? '0-100 KM/H',
      width: '140px',
      sortable: true,
      render: renderTextOrDash,
    },
  ].filter(Boolean) as TableColumn<InventoryTableRow>[];

/**
 * Transform inventory vehicle data to table row format
 * @param vehicle - Raw inventory vehicle from API
 * @returns Formatted table row
 */
export const transformVehicleToRow = (vehicle: InventoryVehicle): InventoryTableRow => {
  // Determine condition based on category/subCategory
  let condition: 'new' | 'demo' | 'pre-owned' = 'new';
  if (vehicle.category === 'demo' || vehicle.subCategory === 'demo') {
    condition = 'demo';
  } else if (vehicle.category === 'pre-owned' || vehicle.subCategory === 'pre-owned') {
    condition = 'pre-owned';
  }

  return {
    vin: vehicle.VIN,
    webLink: vehicle.shortcode,
    batchNumber: vehicle.batchNumber,
    availability: vehicle.availability,
    locationName: vehicle.address?.locationName || '',
    city: vehicle.address?.city || '',
    country: vehicle.address?.country || '',
    model: vehicle.model?.technicalName || '',
    trim: vehicle.modelVariant?.technicalName || '',
    drivetrain: vehicle.motorDrive?.technicalName || '',
    exteriorColor: vehicle.color?.technicalName || '',
    exteriorColorCode: vehicle.color?.sapCode || '',
    interiorColor: vehicle.interior?.technicalName || '',
    interiorColorCode: vehicle.interior?.sapCode || '',
    appearance: vehicle.appearance?.technicalName || '',
    appearanceCode: vehicle.look?.sapCode || '',
    roof: vehicle.roof?.technicalName || '',
    wheels: vehicle.wheels?.technicalName || '',
    wheelsCode: vehicle.wheels?.sapCode || '',
    year: vehicle.modelYear,
    adas: vehicle.driverAssistance?.technicalName || '',
    adasCode: vehicle.driverAssistance?.sapCode || '',
    soundSystem: vehicle.soundSystem?.technicalName || '',
    soundSystemCode: vehicle.soundSystem?.sapCode || '',
    frontSeats: vehicle.seating?.technicalName || '',
    frontSeatsCode: vehicle.seating?.sapCode || '',
    rearSeats: vehicle.rearSeats?.backSeats || '',
    comfortPackage: vehicle.packages?.technicalName || '',
    dynamicHandlingPackage: '',
    technologyPackage: '',
    towingPackage: '',
    range: '',
    power: '',
    zeroToHundredKmh: '',
    transportationConfirmed: '',
    inTransitToLocation: '',
    activeFactoryCampaigns: '',
    // Additional fields for checkout detail request
    uuid: vehicle.uuid,
    shortcode: vehicle.shortcode,
    condition,
    // Pricing fields
    baseMsrp: vehicle.pricing?.baseMSRP,
    price: vehicle.pricing?.totalMSRP,
    destinationCharge: vehicle.pricing?.destinationCharge,
    finalPrice: vehicle.pricing?.finalPrice,
  };
};

/**
 * Transform array of inventory vehicles to table data
 * @param vehicles - Array of inventory vehicles from API
 * @returns Array of formatted table rows
 */
export const transformInventoryData = (vehicles: InventoryVehicle[]): InventoryTableRow[] => {
  return vehicles.map(transformVehicleToRow);
};

const applyPrCodeFilters = (
  filters: InventoryFilters,
  filterState: FilterState,
  prCodes: ParsedFilterOptions | undefined
): void => {
  const mappings = [
    { key: 'exteriorColor', field: 'exteriorColor', options: prCodes?.exteriorColor },
    { key: 'interiorColor', field: 'interiorTheme', options: prCodes?.interiorTheme },
    { key: 'appearance', field: 'appearance', options: prCodes?.appearance },
    { key: 'wheels', field: 'wheels', options: prCodes?.wheels },
    { key: 'adas', field: 'driverAssistance', options: prCodes?.driverAssistance },
    { key: 'soundSystem', field: 'soundSystem', options: prCodes?.soundSystem },
    { key: 'frontSeats', field: 'seating', options: prCodes?.seating },
  ] as const;

  for (const { key, field, options } of mappings) {
    const values = filterState[key];
    if (!Array.isArray(values)) continue;
    const sapCodes = values
      .map((name) => options?.find((option) => option.label === name)?.value)
      .filter((code): code is string => code !== undefined);
    if (sapCodes.length > 0) filters[field] = sapCodes;
  }
};

type PackageFilterField =
  (typeof PACKAGE_FILTER_FIELD_MAPPING)[keyof typeof PACKAGE_FILTER_FIELD_MAPPING];

const applyLegacyPackageFilters = (
  filters: InventoryFilters,
  packages: FilterState[string],
  prCodes: ParsedFilterOptions | undefined
): void => {
  if (!Array.isArray(packages)) return;
  const categories = [
    { field: 'dynamicPackage', options: prCodes?.dynamicHandling },
    { field: 'techPackage', options: prCodes?.technology },
    { field: 'towingPackage', options: prCodes?.towing },
  ] as const;
  const groups: Partial<Record<PackageFilterField, string[]>> = {};

  for (const code of packages) {
    const field =
      categories.find(({ options }) => options?.some((option) => option.value === code))?.field ??
      'vehiclePackage';
    (groups[field] ??= []).push(code);
  }
  for (const [field, values] of Object.entries(groups)) {
    filters[field as PackageFilterField] =
      field === 'vehiclePackage' ? values.flatMap((value) => value.split(',')) : values;
  }
};

/**
 * Convert Table FilterState to InventoryFilters for API calls
 * Maps column-based filters to API parameter format
 */
export const filterStateToInventoryFilters = (
  filterState: FilterState,
  prCodes: ParsedFilterOptions | undefined
): InventoryFilters => {
  const filters: InventoryFilters = { condition: 'new' };
  const modelValues = new Map([
    ['Air', 'AIR'],
    ['Gravity', 'GRAVITY'],
  ]);
  const trimValues = new Map([
    ['Pure', 'PURE'],
    ['Touring', 'AT'],
    ['Grand Touring', 'GT'],
  ]);

  if (typeof filterState.model === 'string' && filterState.model) {
    filters.model = [modelValues.get(filterState.model) ?? filterState.model];
  }
  if (Array.isArray(filterState.trim)) {
    filters.trim = filterState.trim.map((name) => trimValues.get(name) ?? name);
  }
  if (Array.isArray(filterState.availability)) {
    filters.availability = filterState.availability;
  }

  applyPrCodeFilters(filters, filterState, prCodes);
  applyLegacyPackageFilters(filters, filterState.packages, prCodes);

  for (const [key, field] of Object.entries(PACKAGE_FILTER_FIELD_MAPPING)) {
    const values = filterState[key];
    if (Array.isArray(values) && values.length > 0) {
      filters[field] = values.flatMap((value) => value.split(','));
    }
  }

  return filters;
};
